/**
 * ============================================================================
 * AddEquipmentDialog — Phase 6e frontend (Equipment & Validation).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: modal to create new equipment in PLANNED. The category picker
 *          auto-fills criticality / GAMP class / cadence defaults; the user
 *          can override any of them before submit.
 *
 * A11Y: role="dialog" + aria-modal, initial focus on the tag input, Escape
 *  closes. Cadence inputs are number fields with min=1 and aria-describedby
 *  hints so screen readers announce the unit (days).
 * ============================================================================ */

import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@edms/design-system';
import dialogStyles from '../AuthorWorkbench/NewDocumentForm.module.css';
import styles from './EquipmentWorkbench.module.css';
import {
  useCreateEquipmentMutation,
  type CreateEquipmentInput,
} from './useCreateEquipmentMutation';
import type {
  EquipmentCriticality,
  EquipmentSummary,
  GampClass,
} from './useEquipmentQuery';

interface CategoryDefaults {
  key: string;
  labelKey: string;
  defaultLabel: string;
  criticality: EquipmentCriticality;
  gamp_class: GampClass;
  calibration_cadence_days: number;
  pm_cadence_days: number;
  requalification_cadence_days: number;
}

/**
 * Category defaults — mirror the OPS_CFG-EQ-001 seed. The server is still
 * source of truth (we send `category_key`), but the UI shows the picker so
 * the user can see what they're about to create.
 */
const CATEGORIES: CategoryDefaults[] = [
  {
    key: 'HPLC',
    labelKey: 'equipment.workbench.add.categories.HPLC',
    defaultLabel: 'HPLC (chromatograph)',
    criticality: 'CRITICAL',
    gamp_class: 'GAMP5',
    calibration_cadence_days: 180,
    pm_cadence_days: 90,
    requalification_cadence_days: 365,
  },
  {
    key: 'BALANCE',
    labelKey: 'equipment.workbench.add.categories.BALANCE',
    defaultLabel: 'Analytical balance',
    criticality: 'CRITICAL',
    gamp_class: 'GAMP4',
    calibration_cadence_days: 90,
    pm_cadence_days: 180,
    requalification_cadence_days: 730,
  },
  {
    key: 'OVEN',
    labelKey: 'equipment.workbench.add.categories.OVEN',
    defaultLabel: 'Oven / incubator',
    criticality: 'MAJOR',
    gamp_class: 'GAMP3',
    calibration_cadence_days: 180,
    pm_cadence_days: 180,
    requalification_cadence_days: 730,
  },
  {
    key: 'AUTOCLAVE',
    labelKey: 'equipment.workbench.add.categories.AUTOCLAVE',
    defaultLabel: 'Autoclave',
    criticality: 'CRITICAL',
    gamp_class: 'GAMP4',
    calibration_cadence_days: 180,
    pm_cadence_days: 180,
    requalification_cadence_days: 365,
  },
  {
    key: 'PIPETTE',
    labelKey: 'equipment.workbench.add.categories.PIPETTE',
    defaultLabel: 'Micropipette',
    criticality: 'MINOR',
    gamp_class: 'GAMP1',
    calibration_cadence_days: 90,
    pm_cadence_days: 365,
    requalification_cadence_days: 730,
  },
  {
    key: 'THERMOMETER',
    labelKey: 'equipment.workbench.add.categories.THERMOMETER',
    defaultLabel: 'Thermometer',
    criticality: 'MINOR',
    gamp_class: 'GAMP1',
    calibration_cadence_days: 180,
    pm_cadence_days: 365,
    requalification_cadence_days: 730,
  },
  {
    key: 'TABLET_PRESS',
    labelKey: 'equipment.workbench.add.categories.TABLET_PRESS',
    defaultLabel: 'Tablet press',
    criticality: 'CRITICAL',
    gamp_class: 'GAMP5',
    calibration_cadence_days: 180,
    pm_cadence_days: 90,
    requalification_cadence_days: 365,
  },
  {
    key: 'OTHER',
    labelKey: 'equipment.workbench.add.categories.OTHER',
    defaultLabel: 'Other',
    criticality: 'MINOR',
    gamp_class: 'GAMP3',
    calibration_cadence_days: 180,
    pm_cadence_days: 180,
    requalification_cadence_days: 730,
  },
];

const CRIT_OPTIONS: EquipmentCriticality[] = [
  'CRITICAL',
  'MAJOR',
  'MINOR',
  'NON_GMP',
];
const GAMP_OPTIONS: GampClass[] = ['GAMP5', 'GAMP4', 'GAMP3', 'GAMP1'];

export interface AddEquipmentDialogProps {
  onClose: () => void;
  onCreated: (equipment: EquipmentSummary) => void;
}

export function AddEquipmentDialog({
  onClose,
  onCreated,
}: AddEquipmentDialogProps) {
  const { t } = useTranslation();
  const titleId = useId();
  const descId = useId();
  const firstFieldRef = useRef<HTMLInputElement | null>(null);

  const createM = useCreateEquipmentMutation();
  const [categoryKey, setCategoryKey] = useState<string>(CATEGORIES[0]!.key);
  const [tag, setTag] = useState('');
  const [name, setName] = useState('');
  const [manufacturer, setManufacturer] = useState('');
  const [model, setModel] = useState('');
  const [serial, setSerial] = useState('');
  const [siteCode, setSiteCode] = useState('');
  const [location, setLocation] = useState('');
  const [error, setError] = useState<string | null>(null);

  const category = useMemo(
    () => CATEGORIES.find((c) => c.key === categoryKey) ?? CATEGORIES[0]!,
    [categoryKey],
  );

  const [criticality, setCriticality] = useState<EquipmentCriticality>(
    category.criticality,
  );
  const [gampClass, setGampClass] = useState<GampClass>(category.gamp_class);
  const [calCadence, setCalCadence] = useState<number>(
    category.calibration_cadence_days,
  );
  const [pmCadence, setPmCadence] = useState<number>(category.pm_cadence_days);
  const [requalCadence, setRequalCadence] = useState<number>(
    category.requalification_cadence_days,
  );

  // When the category changes, snap overrides back to the category defaults.
  useEffect(() => {
    setCriticality(category.criticality);
    setGampClass(category.gamp_class);
    setCalCadence(category.calibration_cadence_days);
    setPmCadence(category.pm_cadence_days);
    setRequalCadence(category.requalification_cadence_days);
  }, [category]);

  useEffect(() => {
    firstFieldRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function onSubmit(ev: React.FormEvent) {
    ev.preventDefault();
    setError(null);
    if (!tag.trim()) {
      setError(
        t('equipment.workbench.add.errors.tagRequired', {
          defaultValue: 'Equipment tag is required.',
        }),
      );
      return;
    }
    if (!name.trim()) {
      setError(
        t('equipment.workbench.add.errors.nameRequired', {
          defaultValue: 'Equipment name is required.',
        }),
      );
      return;
    }
    const input: CreateEquipmentInput = {
      equipment_tag: tag.trim(),
      name: name.trim(),
      category_key: categoryKey,
      manufacturer: manufacturer.trim() || undefined,
      model: model.trim() || undefined,
      serial: serial.trim() || undefined,
      criticality,
      gamp_class: gampClass,
      calibration_cadence_days: calCadence,
      pm_cadence_days: pmCadence,
      requalification_cadence_days: requalCadence,
      site_code: siteCode.trim() || undefined,
      location: location.trim() || undefined,
    };
    try {
      const res = await createM.mutateAsync(input);
      onCreated(res);
    } catch (err: unknown) {
      setError(
        err instanceof Error
          ? err.message
          : t('equipment.workbench.add.errors.serverFailed', {
              defaultValue: 'Server rejected the create.',
            }),
      );
    }
  }

  return (
    <div
      className={dialogStyles.backdrop}
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={dialogStyles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
      >
        <header className={dialogStyles.header}>
          <h2 id={titleId} className={dialogStyles.title}>
            {t('equipment.workbench.add.title', {
              defaultValue: 'Add equipment',
            })}
          </h2>
          <p id={descId} className={dialogStyles.desc}>
            {t('equipment.workbench.add.subtitle', {
              defaultValue:
                'Create a new equipment record in PLANNED. The category picker pre-fills criticality, GAMP class, and cadence defaults — override any field before submit.',
            })}
          </p>
        </header>

        <form className={dialogStyles.form} onSubmit={onSubmit} noValidate>
          {error && (
            <div role="alert" className={dialogStyles.errorBanner}>
              {error}
            </div>
          )}

          <div className={dialogStyles.field}>
            <label htmlFor="eq-add-cat" className={dialogStyles.label}>
              {t('equipment.workbench.add.category', {
                defaultValue: 'Category',
              })}
              <span className={dialogStyles.required} aria-hidden="true">*</span>
            </label>
            <select
              id="eq-add-cat"
              className={dialogStyles.select}
              value={categoryKey}
              onChange={(e) => setCategoryKey(e.target.value)}
            >
              {CATEGORIES.map((c) => (
                <option key={c.key} value={c.key}>
                  {t(c.labelKey, { defaultValue: c.defaultLabel })}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.helperRow} role="note">
            {t('equipment.workbench.add.categoryHint', {
              defaultValue:
                'Category defaults applied — adjust if this equipment is special.',
            })}
          </div>

          <div className={styles.twoCol}>
            <div className={dialogStyles.field}>
              <label htmlFor="eq-add-tag" className={dialogStyles.label}>
                {t('equipment.workbench.add.tag', {
                  defaultValue: 'Equipment tag',
                })}
                <span className={dialogStyles.required} aria-hidden="true">*</span>
              </label>
              <input
                id="eq-add-tag"
                ref={firstFieldRef}
                className={dialogStyles.input}
                value={tag}
                onChange={(e) => setTag(e.target.value)}
                placeholder="EQ-HPLC-001"
                required
              />
            </div>
            <div className={dialogStyles.field}>
              <label htmlFor="eq-add-name" className={dialogStyles.label}>
                {t('equipment.workbench.add.name', { defaultValue: 'Name' })}
                <span className={dialogStyles.required} aria-hidden="true">*</span>
              </label>
              <input
                id="eq-add-name"
                className={dialogStyles.input}
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
          </div>

          <div className={styles.twoCol}>
            <div className={dialogStyles.field}>
              <label htmlFor="eq-add-manu" className={dialogStyles.label}>
                {t('equipment.workbench.add.manufacturer', {
                  defaultValue: 'Manufacturer',
                })}
              </label>
              <input
                id="eq-add-manu"
                className={dialogStyles.input}
                value={manufacturer}
                onChange={(e) => setManufacturer(e.target.value)}
              />
            </div>
            <div className={dialogStyles.field}>
              <label htmlFor="eq-add-model" className={dialogStyles.label}>
                {t('equipment.workbench.add.model', { defaultValue: 'Model' })}
              </label>
              <input
                id="eq-add-model"
                className={dialogStyles.input}
                value={model}
                onChange={(e) => setModel(e.target.value)}
              />
            </div>
          </div>

          <div className={styles.twoCol}>
            <div className={dialogStyles.field}>
              <label htmlFor="eq-add-serial" className={dialogStyles.label}>
                {t('equipment.workbench.add.serial', { defaultValue: 'Serial' })}
              </label>
              <input
                id="eq-add-serial"
                className={dialogStyles.input}
                value={serial}
                onChange={(e) => setSerial(e.target.value)}
              />
            </div>
            <div className={dialogStyles.field}>
              <label htmlFor="eq-add-site" className={dialogStyles.label}>
                {t('equipment.workbench.add.site', { defaultValue: 'Site' })}
              </label>
              <input
                id="eq-add-site"
                className={dialogStyles.input}
                value={siteCode}
                onChange={(e) => setSiteCode(e.target.value)}
                placeholder="CAI-01"
              />
            </div>
          </div>

          <div className={dialogStyles.field}>
            <label htmlFor="eq-add-loc" className={dialogStyles.label}>
              {t('equipment.workbench.add.location', {
                defaultValue: 'Location',
              })}
            </label>
            <input
              id="eq-add-loc"
              className={dialogStyles.input}
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Lab-A, Room 204"
            />
          </div>

          <div className={styles.twoCol}>
            <div className={dialogStyles.field}>
              <label htmlFor="eq-add-crit" className={dialogStyles.label}>
                {t('equipment.workbench.add.criticality', {
                  defaultValue: 'Criticality',
                })}
              </label>
              <select
                id="eq-add-crit"
                className={dialogStyles.select}
                value={criticality}
                onChange={(e) =>
                  setCriticality(e.target.value as EquipmentCriticality)
                }
              >
                {CRIT_OPTIONS.map((c) => (
                  <option key={c} value={c}>
                    {t(`equipment.criticality.${c}`, { defaultValue: c })}
                  </option>
                ))}
              </select>
            </div>
            <div className={dialogStyles.field}>
              <label htmlFor="eq-add-gamp" className={dialogStyles.label}>
                {t('equipment.workbench.add.gamp', {
                  defaultValue: 'GAMP class',
                })}
              </label>
              <select
                id="eq-add-gamp"
                className={dialogStyles.select}
                value={gampClass}
                onChange={(e) => setGampClass(e.target.value as GampClass)}
              >
                {GAMP_OPTIONS.map((g) => (
                  <option key={g} value={g}>
                    {t(`equipment.gamp.${g}`, { defaultValue: g })}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className={styles.twoCol}>
            <div className={dialogStyles.field}>
              <label htmlFor="eq-add-calc" className={dialogStyles.label}>
                {t('equipment.workbench.add.calCadence', {
                  defaultValue: 'Calibration cadence (days)',
                })}
              </label>
              <input
                id="eq-add-calc"
                type="number"
                min={1}
                className={dialogStyles.input}
                value={calCadence}
                onChange={(e) =>
                  setCalCadence(Math.max(1, Number(e.target.value) || 1))
                }
              />
            </div>
            <div className={dialogStyles.field}>
              <label htmlFor="eq-add-pmc" className={dialogStyles.label}>
                {t('equipment.workbench.add.pmCadence', {
                  defaultValue: 'PM cadence (days)',
                })}
              </label>
              <input
                id="eq-add-pmc"
                type="number"
                min={1}
                className={dialogStyles.input}
                value={pmCadence}
                onChange={(e) =>
                  setPmCadence(Math.max(1, Number(e.target.value) || 1))
                }
              />
            </div>
          </div>

          <div className={dialogStyles.field}>
            <label htmlFor="eq-add-reqc" className={dialogStyles.label}>
              {t('equipment.workbench.add.requalCadence', {
                defaultValue: 'Requalification cadence (days)',
              })}
            </label>
            <input
              id="eq-add-reqc"
              type="number"
              min={1}
              className={dialogStyles.input}
              value={requalCadence}
              onChange={(e) =>
                setRequalCadence(Math.max(1, Number(e.target.value) || 1))
              }
            />
          </div>

          <footer className={dialogStyles.footer}>
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={createM.isPending}
            >
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button type="submit" variant="primary" isLoading={createM.isPending}>
              {t('equipment.workbench.add.submit', {
                defaultValue: 'Create equipment',
              })}
            </Button>
          </footer>
        </form>
      </div>
    </div>
  );
}
